// @ts-check
import { format, parse } from 'date-fns'
import {
  ListIcon,
  NumberIcon,
  SingleLineTextIcon,
  AddressIcon,
  CurrencyIcon,
  SizeIcon,
  CalendarIcon,
  SpaceIcon,
  BuildingIcon,
  SqftIcon,
  PricePerSqftIcon,
  DateAvailableIcon,
  LeaseTermsIcon,
  FormulaIcon,
} from '~/legacy/components/svgs'
import { formatDateAvailableString, formatSize } from './listingHelpers'
import { formatMoney, numberWithCommas, isRangeValue, formatRangeValue } from './numberFormaters'

import { caseInsensitiveCompare } from './miscUtils'

const LIST_SEPARATOR = ','

const parseSqftPrice = (priceString = '') => {
  // match floats or single digits for money
  // eslint-disable-next-line
  const regex = /\d+(\.\d{2})?/g
  const matches = priceString.match(regex)
  if (matches) {
    return matches.pop()
  }
  return null
}

const prepareList = (listValue) => {
  if (listValue) {
    // trim, filter, and dedupe
    const cleanedListValue = listValue
      .map((item) => item.trim())
      .filter((e) => !!e)
    return Array.from(new Set(cleanedListValue))
  }
  return listValue
}

const splitListString = (stringValue) => {
  return stringValue ? stringValue.split(LIST_SEPARATOR) : stringValue
}

const joinListString = (arrayValue) => {
  return arrayValue ? arrayValue.join(LIST_SEPARATOR) : arrayValue
}

const prepareListString = (stringValue) => {
  return stringValue ? prepareList(splitListString(stringValue)) : stringValue
}

const prepareDate = (valueToPrepare, formatString = 'yyyy-MM-dd') => {
  let noTimeRawValue = valueToPrepare
  if (valueToPrepare) {
    if (typeof valueToPrepare === 'string') {
      noTimeRawValue = parse(valueToPrepare, formatString, new Date())
    } else {
      noTimeRawValue = new Date(valueToPrepare.toDateString())
    }
  }

  return noTimeRawValue
}

const prepareDateForAPISave = (valueToPrepare) => {
  const preparedValue = prepareDate(valueToPrepare)
  return preparedValue ? format(preparedValue, 'yyyy-MM-dd') : preparedValue
}

// Validate the provided address
const validateAddress = (newAddress, rawListingData, csvHeaderIndexLookup) => {
  const newGoogleAddress = newAddress ? newAddress.GOOGLE_ADDRESS : null
  const googleAutocompleteResults = newAddress
    ? newAddress.GOOGLE_AUTOCOMPLETE_RESULTS
    : null
  const csvValue = {
    address: rawListingData[csvHeaderIndexLookup.address],
    city: rawListingData[csvHeaderIndexLookup.city],
    state: rawListingData[csvHeaderIndexLookup.state],
    zipcode: rawListingData[csvHeaderIndexLookup.zipcode],
  }

  // This prevents general addresses like a whole city or state
  if (!newGoogleAddress?.address) {
    return {
      value: {},
      csvValue,
      error: "This isn't a valid address.",
    }
  }

  // If we've successfully got a new address from Google api, then use it.
  if (newGoogleAddress) {
    return {
      value: {
        GOOGLE_ADDRESS: newGoogleAddress,
        GOOGLE_AUTOCOMPLETE_RESULTS: googleAutocompleteResults,
      },
      csvValue,
      error: null,
    }
  }
  // Else, we just use whatever the user had in the CSV
  return { value: {}, csvValue, error: "This isn't a valid address." }
}

const validateNumeric = (value) => {
  // TODO: use a proper library like Globalize to handle all of the diff international number formats. It's a bit of a set up process tho
  // Get the value, if its a string, then trim and try to parse out the SQFT bit
  let extractedValue = value
  let extractedFloat = value
  if (typeof value === 'string') {
    extractedValue = value.trim()
    extractedFloat = parseSqftPrice(extractedValue.replaceAll(',', ''))
  }

  // If we've got an empty string, treat field as empty
  if (
    extractedValue === '' ||
    extractedValue === null ||
    extractedValue === undefined
  )
    return { value: null, csvValue: value, error: null }

  // Try to parse the extracted "float" as a float. If its not a number, error
  const parsedFloat = parseFloat(extractedFloat)
  if (isNaN(parsedFloat))
    return {
      value: null,
      csvValue: value,
      error: 'This field expects numbers.',
    }

  // We've got a numeric!
  return { value: parsedFloat, csvValue: value, error: null }
}

const validateList = (value) => {
  if (value === undefined || value === null)
    return { value: null, csvValue: value, error: null }

  // Make the value a string, and then try to parse it as a semicolon separated list
  const valueAsString = String(value)
  if (valueAsString) {
    const splitList = prepareListString(valueAsString)
    return { value: splitList, csvValue: value, error: null }
  }
  return { value: null, csvValue: value, error: null }
}

const validateDate = (value) => {
  if (value === undefined || value === null)
    return { value: null, csvValue: value, error: null }

  if (typeof value === 'string') {
    const stringValue = value.trim()
    // Empty string is just empty
    if (stringValue === '') return { value: null, csvValue: value, error: null }
    // Parse the string as a date, and if its a valid date, we got a good value
    // JS date are the devil
    let newStringValue = stringValue
    if (newStringValue.includes('-')) {
      newStringValue = newStringValue.replaceAll('-', '/')
    }
    const newDate = new Date(Date.parse(newStringValue))
    if (newDate instanceof Date && !isNaN(newDate))
      return { value: newDate, csvValue: value, error: null }
  }

  // If its defined but not a string or a valid date, we've got a problem
  return {
    value: null,
    csvValue: value,
    error: 'This field respects dates.',
  }
}

// The types of the fields, listing or building
const FIELD_TYPES = {
  SPACE: {
    id: 1,
    name: 'Space',
    icon: SpaceIcon,
    templateType: 'SURVEY_SPACES',
  },
  BUILDING: {
    id: 2,
    name: 'Building',
    icon: BuildingIcon,
    templateType: 'SURVEY_BUILDINGS',
  },
}

// The fields we will allow the user to select from for their custom fields.
// Also in order for the picklist
const USER_SELECTABLE_FIELD_TYPES_ORDERED = [
  FIELD_TYPES.BUILDING,
  FIELD_TYPES.SPACE,
]

const defaultFormatter = (value) => value
// The data types of our fields. Each column in the CSV will have one of these types.
const FIELD_DATA_TYPES = {
  STRING: {
    id: 1,
    icon: SingleLineTextIcon,
    name: 'Single Line Text',
    formatter: defaultFormatter,
    validator: (val) => ({ value: val, csvValue: val, error: null }),
    inputType: 'text',
  },
  ADDRESS: {
    id: 2,
    icon: AddressIcon,
    name: 'Address',
    formatter: defaultFormatter,
    validator: validateAddress,
    inputType: 'address',
  },
  NUMBER: {
    id: 3,
    icon: NumberIcon,
    name: 'Number',
    formatter: numberWithCommas,
    validator: validateNumeric,
    inputType: 'number',
  },
  LIST: {
    id: 4,
    icon: ListIcon,
    name: 'Multiselect',
    formatter: (value) => {
      if (!value) return null
      if (Array.isArray(value)) return value.join(',')
      return String(value)
    },
    validator: validateList,
    inputType: 'multi-select',
  },
  SIZE_SQFT: {
    id: 5,
    icon: SizeIcon,
    name: 'Size in sqft',
    // Wrapped (not a direct reference) because bulkImportUtils.js and
    // listingHelpers.js import from each other; capturing formatSize by
    // value here would bake in `undefined` whenever this module happens to
    // load before listingHelpers.js finishes initializing.
    formatter: (value) => formatSize(value),
    validator: validateNumeric,
    inputType: 'number',
  },
  CURRENCY_USD: {
    id: 6,
    icon: CurrencyIcon,
    name: 'Currency',
    formatter: formatMoney,
    validator: validateNumeric,
    inputType: 'number',
  },
  DATE: {
    id: 7,
    icon: CalendarIcon,
    name: 'Date',
    // See SIZE_SQFT above — same cross-file circular-import hazard.
    formatter: (value) => formatDateAvailableString(value),
    validator: validateDate,
    inputType: 'date',
  },
  MULTILINE_STRING: {
    id: 8,
    icon: SingleLineTextIcon,
    name: 'Multiline Text',
    formatter: defaultFormatter,
    validator: (val) => ({ value: val, csvValue: val, error: null }),
    inputType: 'multiline-text',
  },
}

const FIELD_DATA_TYPES_LOOKUP = Object.fromEntries(
  Object.values(FIELD_DATA_TYPES).map((fieldType) => [fieldType.id, fieldType])
)

export const getIconByDataTypeId = (id) => {
  // Special case for formula field type
  if (id === FORMULA_FIELD_DATA_TYPE.id) {
    return FORMULA_FIELD_DATA_TYPE.icon
  }

  const fieldData = Object.values(FIELD_DATA_TYPES).find(
    (value) => value.id === id
  )
  if (fieldData.icon) {
    return fieldData.icon
  }
  return null
}

export const getDataTypeNameByDataTypeId = (id) => {
  const fieldData = Object.values(FIELD_DATA_TYPES).find(
    (value) => value.id === id
  )
  if (fieldData.name) {
    return fieldData.name
  }
  return null
}

const NUMERIC_FIELD_DATA_TYPE_IDS = new Set([
  FIELD_DATA_TYPES.NUMBER.id,
  FIELD_DATA_TYPES.CURRENCY_USD.id,
  FIELD_DATA_TYPES.SIZE_SQFT.id,
])

// The CSV column fields used for the composite address field
const ADDRESS_FIELD_PARTS = {
  ADDRESS: 'address',
  CITY: 'city',
  STATE: 'state',
  ZIPCODE: 'zipcode',
  LATITUDE: 'latitude',
  LONGITUDE: 'longitude',
}

// The fields we will allow the user to select from for their custom fields.
// Also in order for the picklist
const USER_SELECTABLE_FIELD_DATA_TYPES_ORDERED = [
  FIELD_DATA_TYPES.NUMBER,
  FIELD_DATA_TYPES.STRING,
  FIELD_DATA_TYPES.MULTILINE_STRING,
  FIELD_DATA_TYPES.CURRENCY_USD,
  FIELD_DATA_TYPES.SIZE_SQFT,
  FIELD_DATA_TYPES.DATE,
  FIELD_DATA_TYPES.LIST,
]

// Our special fields defined individually
// displayName: Name displayed to the user
// matches: array of strings to match to the csv column headers, case insensitive
// fieldDataType: The data type of the field. String, number etc.
// icon: Icon for the field
// fieldType: The type of the field, building or space
// modelFieldName: The name of the field on the building/space model that matches this field
// reserved: Whether or not this field's metadat can be changed. These are Leaseup reserved/standard fields
const DEFAULT_PLACEHOLDER_STRING = 'Enter a value'
const FIELDS = {
  // reserved/standard fields
  SIZE: {
    fieldId: -5,
    displayName: 'SqFt Available',
    matches: ['SqFt Available', 'Total Available Space (SF)'],
    fieldDataType: FIELD_DATA_TYPES.SIZE_SQFT,
    icon: SqftIcon,
    fieldType: FIELD_TYPES.SPACE,
    reserved: true,
    modelFieldName: 'size',
  },
  PRICE: {
    fieldId: -4,
    displayName: 'Price / SqFt',
    matches: ['Rent/SF/Yr', 'Price / SqFt'],
    fieldDataType: FIELD_DATA_TYPES.CURRENCY_USD,
    icon: PricePerSqftIcon,
    fieldType: FIELD_TYPES.SPACE,
    reserved: true,
    modelFieldName: 'sqft_price',
  },
  DATE_AVAILABLE: {
    fieldId: -3,
    displayName: 'Date Available',
    matches: ['Date Available'],
    fieldDataType: FIELD_DATA_TYPES.DATE,
    icon: DateAvailableIcon,
    fieldType: FIELD_TYPES.SPACE,
    reserved: true,
    modelFieldName: 'date_available',
  },
  LEASE_TERM: {
    fieldId: -1,
    displayName: 'Lease Term',
    matches: ['Lease Term'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    icon: LeaseTermsIcon,
    fieldType: FIELD_TYPES.SPACE,
    reserved: true,
    modelFieldName: 'lease_term',
  },
  ADDRESS: {
    displayName: 'Address',
    csvString: ADDRESS_FIELD_PARTS.ADDRESS,
    fieldDataType: FIELD_DATA_TYPES.ADDRESS,
    index: -3,
    reserved: true,
    disableAllEditing: true,
    fieldType: FIELD_TYPES.BUILDING,
    modelFieldName: 'address',
  },
  SPACE_NOTES: {
    fieldId: -7,
    index: -1,
    displayName: 'Space Notes',
    matches: ['Space Notes', 'Listing Notes'],
    fieldDataType: FIELD_DATA_TYPES.MULTILINE_STRING,
    fieldType: FIELD_TYPES.SPACE,
    icon: SingleLineTextIcon,
    modelFieldName: 'description',
    placeholderText: 'Add Note',
    reserved: true,
    standard: true,
    matchMenuOrder: 5,
  },

  // Suggested fields
  AMENITIES: {
    fieldId: -6,
    displayName: 'Amenities',
    matches: ['Amenities'],
    fieldDataType: FIELD_DATA_TYPES.LIST,
    fieldType: FIELD_TYPES.BUILDING,
    icon: ListIcon,
    modelFieldName: 'amenities',
    reserved: true,
    standard: true,
    matchMenuOrder: 3,
  },
  SPACE_NAME: {
    fieldId: -8,
    index: -2,
    displayName: 'Space Name',
    matches: ['Space Name'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.SPACE,
    icon: SingleLineTextIcon,
    modelFieldName: 'address2',
    reserved: true,
    standard: true,
    matchMenuOrder: 4,
  },
  FLOORS: {
    fieldId: -2,
    displayName: 'Floors',
    matches: ['Floors'],
    fieldDataType: FIELD_DATA_TYPES.NUMBER,
    fieldType: FIELD_TYPES.BUILDING,
    modelFieldName: 'floors',
  },
  VACANCY_TYPE: {
    fieldId: -6,
    displayName: 'Vacancy Type',
    matches: ['Vacancy Type'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.SPACE,
    modelFieldName: 'vacancy_type',
  },
  LEASE_TYPE: {
    fieldId: -2,
    displayName: 'Lease Type',
    matches: ['Lease Type'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.SPACE,
    modelFieldName: 'lease_type',
  },
  PROPERTY_TYPE: {
    fieldId: -1,
    displayName: 'Property Type',
    matches: ['Property Type', 'PropertyType'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
    modelFieldName: 'property_type',
  },
  BUILDING_SIZE: {
    fieldId: -3,
    displayName: 'Building Size',
    matches: ['Building Size', 'BuildingSize', 'RBA'],
    fieldDataType: FIELD_DATA_TYPES.SIZE_SQFT,
    fieldType: FIELD_TYPES.BUILDING,
    modelFieldName: 'building_size',
  },
  PARKING_RATIO: {
    fieldId: -4,
    displayName: 'Parking Ratio',
    matches: ['Parking Ratio'],
    fieldDataType: FIELD_DATA_TYPES.NUMBER,
    fieldType: FIELD_TYPES.BUILDING,
    modelFieldName: 'parking_ratio',
  },
  BUILDING_DESCRIPTION: {
    fieldId: -5,
    displayName: 'Building Description',
    matches: ['Building Notes', 'Building Description'],
    fieldDataType: FIELD_DATA_TYPES.MULTILINE_STRING,
    fieldType: FIELD_TYPES.BUILDING,
    icon: SingleLineTextIcon,
    modelFieldName: 'description',
    reserved: true,
    standard: true,
    matchMenuOrder: 2,
  },
  BUILDING_NAME: {
    fieldId: -7,
    displayName: 'Building Name',
    matches: ['Building Name'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
    icon: SingleLineTextIcon,
    modelFieldName: 'name',
    reserved: true,
    standard: true,
    matchMenuOrder: 1,
  },
}

const BUILDING_FIELDS = Object.values(FIELDS).filter(
  (field) => field.fieldType === FIELD_TYPES.BUILDING
)
const SPACE_FIELDS = Object.values(FIELDS).filter(
  (field) => field.fieldType === FIELD_TYPES.SPACE
)

const FIELDS_LOOKUP = {
  'SqFt Available': FIELDS.SIZE,
  'Price / SqFt': FIELDS.PRICE,
  'Date Available': FIELDS.DATE_AVAILABLE,
  'Lease Term': FIELDS.LEASE_TERM,
  Address: FIELDS.ADDRESS,
  Amenities: FIELDS.AMENITIES,
  'Space Name': FIELDS.SPACE_NAME,
  Floors: FIELDS.FLOORS,
  'Vacancy Type': FIELDS.VACANCY_TYPE,
  'Lease Type': FIELDS.LEASE_TYPE,
  'Property Type': FIELDS.PROPERTY_TYPE,
  'Building Size': FIELDS.BUILDING_SIZE,
  'Parking Ratio': FIELDS.PARKING_RATIO,
  'Space Notes': FIELDS.SPACE_NOTES,
  'Building Description': FIELDS.BUILDING_DESCRIPTION,
  'Building Notes': FIELDS.BUILDING_DESCRIPTION,
  'Building Name': FIELDS.BUILDING_NAME,
}

// Array of our special fields. We will use these fields to suggest the type of
// the match.
const SUGGESTED_FIELDS = [
  // Building fields
  FIELDS.AMENITIES,
  FIELDS.BUILDING_SIZE,
  FIELDS.PARKING_RATIO,
  FIELDS.FLOORS,
  FIELDS.PROPERTY_TYPE,

  // New suggested fields until we have the smart way of doing it based on existing data/templates from the customer
  // For now all fields are suggested as STRING type
  {
    displayName: 'Year Built',
    matches: ['Year Built'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Building Class',
    matches: ['Building Class'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Submarket Name',
    matches: ['Submarket Name'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Zip',
    matches: ['Zip'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Number Of Stories',
    matches: ['Number Of Stories'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Typical Floor Size',
    matches: ['Typical Floor Size'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Year Renovated',
    matches: ['Year Renovated'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Percent Leased',
    matches: ['Percent Leased'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Building Status',
    matches: ['Building Status'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'County Name',
    matches: ['County Name'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Building Name',
    matches: ['Building Name'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Submarket Cluster',
    matches: ['Submarket Cluster'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Parking',
    matches: ['Parking'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Owner Name',
    matches: ['Owner Name'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'True Owner Name',
    matches: ['True Owner Name'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Parking Ratio & Type',
    matches: ['Parking Ratio & Type'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Owner',
    matches: ['Owner'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Number Of Parking Spaces',
    matches: ['Number Of Parking Spaces'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Zoning',
    matches: ['Zoning'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Tenancy',
    matches: ['Tenancy'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Building Type',
    matches: ['Building Type'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Clear Height',
    matches: ['Clear Height'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Submarket',
    matches: ['Submarket'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Building Operating Expenses',
    matches: ['Building Operating Expenses'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Market Name',
    matches: ['Market Name'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Landlord',
    matches: ['Landlord'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Power',
    matches: ['Power'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Sprinklers',
    matches: ['Sprinklers'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Building Owner',
    matches: ['Building Owner'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Ceiling Ht',
    matches: ['Ceiling Ht'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Drive Ins',
    matches: ['Drive Ins'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Class',
    matches: ['Class'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Number Of Loading Docks',
    matches: ['Number Of Loading Docks'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Number of floors',
    matches: ['Number of floors'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Location',
    matches: ['Location'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Column Spacing',
    matches: ['Column Spacing'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Pkg Ratio & Type',
    matches: ['Pkg Ratio & Type'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Office Area (SF)',
    matches: ['Office Area (SF)'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'For Sale Status',
    matches: ['For Sale Status'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Typical Floor',
    matches: ['Typical Floor'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Building SF / Stories',
    matches: ['Building SF / Stories'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Node/Municipality',
    matches: ['Node/Municipality'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Leasing District',
    matches: ['Leasing District'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Land Area (AC)',
    matches: ['Land Area (AC)'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Total Available Rate',
    matches: ['Total Available Rate'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Built/Renovated',
    matches: ['Built/Renovated'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Max Contiguous',
    matches: ['Max Contiguous'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'True Owner',
    matches: ['True Owner'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'RBA (% Leased)',
    matches: ['RBA (% Leased)'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Rating',
    matches: ['Rating'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Name',
    matches: ['Name'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Centre Type',
    matches: ['Centre Type'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Owner Type',
    matches: ['Owner Type'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Owner Occupied',
    matches: ['Owner Occupied'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Center Size',
    matches: ['Center Size'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Traffic Count',
    matches: ['Traffic Count'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Elevators',
    matches: ['Elevators'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Cap Rate',
    matches: ['Cap Rate'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },
  {
    displayName: 'Stories',
    matches: ['Stories'],
    fieldDataType: FIELD_DATA_TYPES.STRING,
    fieldType: FIELD_TYPES.BUILDING,
  },

  // Space fields
  FIELDS.SPACE_NAME,
  FIELDS.VACANCY_TYPE,
  FIELDS.LEASE_TYPE,
]

// Our standard fields across listings. They are reserved and their metadata cannot be changed
//   by the user.
const STANDARD_FIELDS = Object.values(FIELDS).filter(
  (f) => f.reserved && !f.standard && f.index !== FIELDS.ADDRESS.index
)

const NEW_STANDARD_FIELDS = Object.values(FIELDS)
  .filter((f) => f.standard)
  .sort((a, b) => a.matchMenuOrder - b.matchMenuOrder)

// Reserved leaseup fields. We control their type, display name, etc.
const RESERVED_FIELDS = [FIELDS.ADDRESS, ...STANDARD_FIELDS]
const NEW_RESERVED_FIELDS = [FIELDS.ADDRESS, ...NEW_STANDARD_FIELDS]

// The CSV fields that will be used to build the address
const ADDRESS_FIELDS = [
  {
    matches: ['Property Address', 'Address', 'Property'],
    newFieldName: ADDRESS_FIELD_PARTS.ADDRESS,
  },
  {
    matches: ['City'],
    newFieldName: ADDRESS_FIELD_PARTS.CITY,
  },
  {
    matches: ['State'],
    newFieldName: ADDRESS_FIELD_PARTS.STATE,
  },
  {
    matches: ['Zipcode'],
    newFieldName: ADDRESS_FIELD_PARTS.ZIPCODE,
  },
  {
    matches: ['Latitude', 'Lat'],
    newFieldName: ADDRESS_FIELD_PARTS.LATITUDE,
  },
  {
    matches: ['Longitude', 'Lng', 'Long'],
    newFieldName: ADDRESS_FIELD_PARTS.LONGITUDE,
  },
]
// Separate formula field data type
// it's only used in bulk edit (spaces)
const FORMULA_FIELD_DATA_TYPE = {
  id: 9,
  icon: FormulaIcon,
  name: 'Formula',
  formatter: (value) => BULK_IMPORT_HELPERS.getNumberValueForDisplay(value, 9),
  validator: (val) => ({ value: val, csvValue: val, error: null }),
  inputType: 'text',
}

// Fields we will strip from the csv data since they are used for special composite fields
// and background-only processing fields (ie: lat/lng).
// ie: city, address, state, zipcode, latitude, longitude
const SPECIAL_FIELDS_TO_STRIP = ADDRESS_FIELDS.map(
  (addressField) => addressField.matches
).flat()

export const getDataTypeByDisplayName = (
  displayName,
  defaultDataType = FIELD_DATA_TYPES.STRING
) => {
  const fieldData = FIELDS_LOOKUP[displayName]
  return fieldData ? fieldData.fieldDataType : defaultDataType
}

export const getPlaceholderTextByDisplayName = (displayName) => {
  const fieldData = FIELDS_LOOKUP[displayName]
  return fieldData
    ? fieldData.placeholderText || DEFAULT_PLACEHOLDER_STRING
    : DEFAULT_PLACEHOLDER_STRING
}

export const getModelFieldNameByDisplayName = (displayName) => {
  const fieldData = FIELDS_LOOKUP[displayName]
  return fieldData ? fieldData.modelFieldName : null
}

// Validate the raw value, signal if there is an error with the value
export const getValidatedFieldValue = (
  field,
  validatedListingData,
  rawListingData,
  csvHeaderIndexLookup
) => {
  const value = validatedListingData[field.index]
  const rawValue = value ? value.csvValue : null

  if (field.fieldDataType.id === FIELD_DATA_TYPES.ADDRESS.id) {
    return validateAddress(
      value ? value.value : null,
      rawListingData,
      csvHeaderIndexLookup
    )
  }

  if (
    field.fieldDataType.id === FIELD_DATA_TYPES.STRING.id ||
    field.fieldDataType.id === FIELD_DATA_TYPES.MULTILINE_STRING.id
  ) {
    return { value: rawValue, csvValue: rawValue, error: null }
  }
  if (
    field.fieldDataType.id === FIELD_DATA_TYPES.NUMBER.id ||
    field.fieldDataType.id === FIELD_DATA_TYPES.SIZE_SQFT.id ||
    field.fieldDataType.id === FIELD_DATA_TYPES.CURRENCY_USD.id
  ) {
    return validateNumeric(rawValue)
  }

  if (field.fieldDataType.id === FIELD_DATA_TYPES.LIST.id) {
    return validateList(rawValue)
  }

  if (field.fieldDataType.id === FIELD_DATA_TYPES.DATE.id) {
    const result = validateDate(rawValue)
    return result
  }

  return {
    value: rawValue,
    csvValue: rawValue,
    error: "This isn't a valid value.",
  }
}

// Match a CSV field with one of our standard fields
const matchMappableField = (
  field,
  csvField,
  fieldsWithMatches,
  setFieldsWithMatches,
  mappedStandardFields,
  setMappedStandardFields,
  mappableFieldIndex
) => {
  const csvFieldName = csvField.csvString
  // Match the csv field with the mappable field by overwriting the display name and type
  // First, unset any previous matches to this special field
  const newfieldsWithMatches = Object.fromEntries(
    Object.values(fieldsWithMatches).map((fieldWithMatches) => {
      return [
        fieldWithMatches.index,
        caseInsensitiveCompare(field.displayName, fieldWithMatches.displayName)
          ? {
              ...fieldWithMatches,
              displayName: fieldWithMatches.csvString,
              match: null,
              icon: null,
              reserved: false,
              standard: false,
            }
          : fieldWithMatches,
      ]
    })
  )

  // Replace the match
  newfieldsWithMatches[csvField.index] = {
    ...newfieldsWithMatches[csvField.index],
    csvString: csvFieldName,
    displayName: field.displayName,
    fieldDataType: field.fieldDataType,
    fieldType: field.fieldType,
    // FIXME: I din't want to add the reserved field on the FIELDS object to prevent
    // possibly breaking the importer for non flagged surveys.
    // This way we use the newly added `standard` property for flagged surveys
    // once all are migrated we can consolidate the actual reserved fields
    reserved: field.reserved || field.standard,
    match: csvField.index,
  }

  // Update our mappable fields with the match to the csv field. We unset any previous matches to this field, as there
  //   can only be one match to a csv column.
  const newMappedStandardFields = [...mappedStandardFields].map(
    (mappedStandardField) =>
      mappedStandardField.match === csvField.index
        ? { ...mappedStandardField, match: null }
        : mappedStandardField
  )
  newMappedStandardFields[mappableFieldIndex] = {
    ...field,
    match: csvField.index,
  }

  setMappedStandardFields(newMappedStandardFields)
  setFieldsWithMatches(newfieldsWithMatches)
}

// Handler for after the csv data has been loaded
const onCsvFileLoaded = (
  results,
  setLoadedListingsFromCsv,
  setRawCsvHeaders,
  setBulkImportFieldMatchModalOpen
) => {
  setLoadedListingsFromCsv(results)
  if (results && results.length) {
    setRawCsvHeaders(Object.keys(results[0]))
  }
  setBulkImportFieldMatchModalOpen(true)
}

// Parser options for csv uploader
const CSV_UPLOAD_PARSER_OPTIONS = {
  header: true,
  dynamicTyping: true,
  skipEmptyLines: true,
  transformHeader: (headerIn) => {
    const strippedHeader = headerIn ? headerIn.trim() : headerIn
    const matchedField = ADDRESS_FIELDS.find((field) =>
      field.matches.find(
        (fMatch) =>
          fMatch.localeCompare(strippedHeader, undefined, {
            sensitivity: 'base',
          }) === 0
      )
    )
    return matchedField ? matchedField.newFieldName : strippedHeader
  },
  complete: () => {},
}

export const getNumberValueForDisplay = (value, dataType) => {
  if (isRangeValue(value)) {
    if (dataType === FIELD_DATA_TYPES.SIZE_SQFT.id) {
      const joined = formatRangeValue(value, (n) => Number(n).toLocaleString())
      return joined === null ? null : `${joined} sqft`
    }
    return formatRangeValue(value, (n) => getNumberValueForDisplay(n, dataType))
  }

  let displayValue = value
  if (value !== null && value !== undefined) {
    displayValue = Number(displayValue)
    if (dataType === FIELD_DATA_TYPES.CURRENCY_USD.id) {
      displayValue = displayValue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    } else {
      displayValue = displayValue.toLocaleString()
    }

    if (dataType === FIELD_DATA_TYPES.SIZE_SQFT.id) {
      displayValue = `${displayValue} sqft`
    }
    // Special case for formula fields (ID 9)
    if (
      dataType === FIELD_DATA_TYPES.CURRENCY_USD.id ||
      dataType === FORMULA_FIELD_DATA_TYPE.id
    ) {
      displayValue = `$${displayValue}`
    }
  }

  return displayValue
}

const getFieldDataTypeById = (fieldDataTypeId) => {
  // Special case for formula fields (ID 9)
  if (fieldDataTypeId === FORMULA_FIELD_DATA_TYPE.id) {
    return FORMULA_FIELD_DATA_TYPE
  }

  return Object.values(FIELD_DATA_TYPES).find(
    (fieldDataType) => fieldDataType.id === fieldDataTypeId
  )
}

export const BULK_IMPORT_CONSTANTS = {
  FIELD_TYPES,
  USER_SELECTABLE_FIELD_TYPES_ORDERED,
  FIELD_DATA_TYPES,
  FIELD_DATA_TYPES_LOOKUP,
  FIELDS,
  BUILDING_FIELDS,
  SPACE_FIELDS,
  ADDRESS_FIELD_PARTS,
  USER_SELECTABLE_FIELD_DATA_TYPES_ORDERED,
  SUGGESTED_FIELDS,
  STANDARD_FIELDS,
  NEW_STANDARD_FIELDS,
  RESERVED_FIELDS,
  NEW_RESERVED_FIELDS,
  SPECIAL_FIELDS_TO_STRIP,
  ADDRESS_FIELDS,
  CSV_UPLOAD_PARSER_OPTIONS,
  NUMERIC_FIELD_DATA_TYPE_IDS,
  DEFAULT_PLACEHOLDER_STRING,
  FORMULA_FIELD_DATA_TYPE,
}

export const BULK_IMPORT_VALIDATORS = {
  validateAddress,
  validateNumeric,
  validateList,
  validateDate,
}

export const BULK_IMPORT_HELPERS = {
  matchMappableField,
  onCsvFileLoaded,
  getNumberValueForDisplay,
  prepareList,
  prepareListString,
  splitListString,
  prepareDate,
  getFieldDataTypeById,
  joinListString,
  prepareDateForAPISave,
}
